import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'

/** The row itself is usually clickable; pressing an action is not pressing the
 *  row. Declared once rather than per row — a list runs to hundreds. */
const swallowPress = (event: ReactMouseEvent) => event.stopPropagation()

const SHELL =
  'absolute inset-y-0 right-0 flex flex-row items-center opacity-0 transition-opacity ' +
  // Invisible actions must not swallow the click meant for the row.
  'pointer-events-none group-hover/row:pointer-events-auto group-hover/row:opacity-100 ' +
  // The row carries the keyboard focus; the buttons inside carry it once they
  // have been tabbed into. An open menu holds them out while the pointer is
  // away in the portal it renders in.
  'group-focus-visible/row:pointer-events-auto group-focus-visible/row:opacity-100 ' +
  'has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:opacity-100 ' +
  'has-[[data-state=open]]:pointer-events-auto has-[[data-state=open]]:opacity-100'

/** A bar of buttons is laid over the row and reads as lifted off it; a single
 *  icon button is the control itself, so it stays close to the surface and
 *  gives up all but the hair of inset that keeps its own hover ground inside
 *  the frame. The radii nest: the outer one is the button's plus that inset. */
const SURFACE =
  'relative flex flex-row items-center gap-1.5 border border-border bg-card'
const SURFACE_BY_VARIANT = {
  bar: `${SURFACE} rounded-md p-1 shadow-lg`,
  single: `${SURFACE} rounded-lg p-0.5 shadow-sm`,
} as const

/**
 * A list row's actions, revealed at its trailing edge while the row is pointed
 * at or holds focus.
 *
 * Three things make it readable over content it necessarily covers: it sits on
 * a raised surface of its own rather than on the row's ground, the row's own
 * text dissolves into a fade rather than being cut mid-word, and it is opaque
 * from the fade rightwards so nothing shows through the buttons.
 *
 * Revealed for the keyboard as well as the pointer, because actions only a
 * mouse can reach are actions a keyboard user does not have — but on
 * `focus-visible`: a row takes focus from the click that opened it, and plain
 * focus would leave its actions standing over the list until something else
 * was pressed.
 *
 * The row must be `relative`, carry `group/row`, and declare the colour it is
 * painted in as `--row-ground`, which is what the fade resolves to. `listRow`
 * does all three, so a `TableRow` and a `DataTable` row need nothing.
 *
 * Every backing is the card surface with the row's ground over it, because a
 * ground may be an alpha — `selected` is — and a wash that thin hides nothing
 * on its own.
 */
export function RowActions({
  children,
  variant = 'bar',
}: {
  children: ReactNode
  /** `single` fits the surface to one icon button; `bar` holds a row of them. */
  variant?: keyof typeof SURFACE_BY_VARIANT
}) {
  return (
    <span onClick={swallowPress} className={SHELL}>
      {/* Two layers rather than one gradient with a stop: a stop in absolute
          length is not a utility Tailwind emits, and a percentage would move
          with however wide the actions turn out to be. */}
      <span className="relative h-full w-12 bg-linear-to-r from-transparent to-card">
        <span
          aria-hidden
          className="absolute inset-0 bg-linear-to-r from-transparent to-[var(--row-ground)]"
        />
      </span>
      <span className="relative flex h-full flex-row items-center bg-card pr-4">
        <span aria-hidden className="absolute inset-0 bg-[var(--row-ground)]" />
        <span className={SURFACE_BY_VARIANT[variant]}>{children}</span>
      </span>
    </span>
  )
}
